// ── SHOULD THIS PHONE BE ASKED TO ADD ROOFBEAM TO ITS HOME SCREEN? ──────────
//
// ⚠ THE REASON THIS MATTERS MORE THAN A NICER ICON. On iOS, web push works
// ONLY from the Home Screen app — in Safari, permission cannot be granted at
// all (see src/utils/webPush.js). So "add it to your Home Screen" is not a
// cosmetic suggestion to an iPhone crew member: it is the ONLY route to ever
// being notified of a job or a schedule change. The card says that.
//
// ⚠ AND iOS CANNOT BE PROMPTED. There is no beforeinstallprompt on iOS and no
// API that installs a web app — Apple requires the person to do it by hand
// through the Share sheet. That is why this is a set of INSTRUCTIONS on iOS
// and a real BUTTON on Android, and why the two are different components of
// the same card rather than one "Install" button that would do nothing on half
// the phones.
//
// Pure: no React, no Firestore. The caller owns the UI.

const DISMISS_KEY = 'roofbeam.a2hs.dismissedUntil';   // ms epoch, or 'forever'
const SNOOZE_DAYS = 7;

export const A2HS = {
  HIDE: 'hide',                 // already installed, native, desktop, dismissed
  IOS_SAFARI: 'ios-safari',     // the Share → Add to Home Screen steps
  IOS_OTHER: 'ios-other',       // Chrome/Firefox/Edge on iOS — only Safari can
  ANDROID: 'android',           // the app download, or the browser's own prompt
};

const ua = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent || '');

export function isIos() {
  const u = ua();
  if (/iPad|iPhone|iPod/.test(u)) return true;
  // iPadOS 13+ reports a Macintosh UA in desktop mode; touch points are the
  // only reliable tell, and without this an iPad is told it is a desktop.
  return /Macintosh/.test(u) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
}

export const isAndroid = () => /Android/.test(ua());

// ⚠ EVERY BROWSER ON iOS IS SAFARI UNDERNEATH, and none of the others can add
// a web app to the Home Screen — Chrome/Firefox/Edge/Opera on iOS have no
// "Add to Home Screen" in their share sheets at all. So they get their own
// state telling them to open the page in Safari, rather than a set of steps
// they cannot follow.
export function isIosSafari() {
  if (!isIos()) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|mercury|GSA\//i.test(ua());
}

export function isStandalone() {
  try {
    if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || window.matchMedia('(display-mode: minimal-ui)').matches;
    }
  } catch (_) { /* a matchMedia that throws is not a reason to nag */ }
  return false;
}

// A phone, not a desktop. ⚠ Checked on the USER AGENT rather than the window
// width: a narrow desktop window is still a desktop, and telling somebody at a
// laptop to tap their Share button is the kind of thing that makes a whole
// product feel stupid.
export function isPhone() {
  return isIos() || isAndroid();
}

// ── DISMISSAL ───────────────────────────────────────────────────────────────
// "Not now" → 7 days. "Done" → never again on this device.
// ⚠ A STORAGE FAILURE MEANS HIDE, NOT SHOW. In private mode, or with site data
// blocked, the dismissal cannot be written — and a card that reappears on
// every single login because it cannot remember being dismissed is far worse
// than one that is never shown.
export function dismissalState(now = Date.now()) {
  try {
    const v = window.localStorage.getItem(DISMISS_KEY);
    if (!v) return { dismissed: false, forever: false };
    if (v === 'forever') return { dismissed: true, forever: true };
    const until = Number(v);
    if (!Number.isFinite(until)) return { dismissed: false, forever: false };
    return { dismissed: now < until, forever: false, until };
  } catch (_) {
    return { dismissed: true, forever: false, unreadable: true };
  }
}

export function snooze(days = SNOOZE_DAYS, now = Date.now()) {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(now + days * 24 * 60 * 60 * 1000));
    return true;
  } catch (_) { return false; }
}

export function dismissForever() {
  try {
    window.localStorage.setItem(DISMISS_KEY, 'forever');
    return true;
  } catch (_) { return false; }
}

// Test/debug helper — also what a "show it to me again" control would call.
export function clearDismissal() {
  try { window.localStorage.removeItem(DISMISS_KEY); return true; } catch (_) { return false; }
}

// ── THE DECISION ────────────────────────────────────────────────────────────
// -> one of A2HS.*. Order matters: the things that make the card WRONG come
// before the things that make it unwanted.
export function a2hsState({ native = false, now = Date.now() } = {}) {
  // ⚠ NEVER INSIDE THE APP. The Android app and an already-installed Home
  // Screen app are both "already done" — a card telling somebody to install
  // the thing they are holding is the single most irritating outcome here.
  if (native) return A2HS.HIDE;
  if (isStandalone()) return A2HS.HIDE;
  if (!isPhone()) return A2HS.HIDE;
  if (dismissalState(now).dismissed) return A2HS.HIDE;
  if (isIos()) return isIosSafari() ? A2HS.IOS_SAFARI : A2HS.IOS_OTHER;
  if (isAndroid()) return A2HS.ANDROID;
  return A2HS.HIDE;
}

// The words, kept here rather than in the component so the steps and the
// reason cannot drift from webPush.js's own copy of the same instruction.
export const IOS_STEPS = [
  { icon: '⬆️', text: 'Tap the Share button at the bottom of Safari' },
  { icon: '➕', text: 'Scroll down and tap "Add to Home Screen"' },
  { icon: '✅', text: 'Tap "Add" in the top right' },
  { icon: '🏠', text: 'Open RoofBeam from the new icon on your Home Screen' },
];
